import * as d3 from "d3";
import $ from "jquery";
import {_does, uMath} from "src/components/Planner/planner/uMath"
import {ts, ts2} from "src/utils/translate"
import {SVG_TEXT_HELPER} from "src/utils/svgTextHelper";
import {getFrontendURL} from "src/utils/url";

/**
 * @typedef {Object} PinoutDevice
 * @property {string} id
 * @property {string} text
 * @property {string} imageUrl
 * @property {number} originalImageWidth
 * @property {number} originalImageHeight
 * @property {number} railTop
 * @property {number} railBot
 * @property {number} height
 * @property {number} width
 * @property {number} marginLeft
 * @property {number} marginRight
 * @property {number} drawnX
 * @property {number} drawnY
 * @property {number} bottomY
 * @property {Port[]} inputs
 * @property {Port[]} outputs
 * @property {Port[]} ports inputs + outputs
 * @property {Connection[]} connections
 * @property {string[][]} menorahGroups
 * @property {boolean?} isIT
 */

/**
 * @typedef {Object} BusWithExtensions
 * @property {string} name
 * @property {BusExtension[]} extensions
 */

/**
* @typedef {Object} BusExtension
* @property {string} name
* @property {string} id
* @property {BusLine[]} lines
*/

/**
* @typedef {Object} BusLine
* @property {string} name
* @property {string} id
* @property {BusWire[]} wires
*/

/**
* @typedef {Object} BusWire
* @property {string} id
* @property {string} type
* @property {BusSimpleDevice[]} devices
*/

/**
* @typedef {Object} BusSimpleDevice
* @property {string} image
* @property {string} text
*/


/**
* @typedef {Object} Port
* @property {string} portID
* @property {number} xPosition modified X position of port on dev (eg top left)
* @property {number} yPosition modified Y position of port on dev (eg top left)
* @property {number} xOriginPosition original Y position of port on dev
* @property {number} yOriginPosition original Y position of port on dev
* @property {Label} label
* @property {string} portOrientation
* @property {number?} lengthMult
*/

/**
 * @typedef {Object} PortOfDevice
 * @property {string} deviceId
 * @property {string} portId
 */

/**
 * @typedef {Object} Cable
 * @property {string} cableID
 * @property {string} cableName
 * @property {string} cableTypeID
 * @property {string} cableColor
 * @property {PortOfDevice} beginningPort
 * @property {PortOfDevice} endingPort
 * @property {Port[]} connectedPorts
 * @property {"up" | "down"} cableOrientation
 */

/**
 * @typedef {Object} CableType
 * @property {string} cableTypeID
 * @property {string} displayName
 */

/**
 * @typedef {Object} Connection
 * @property {string} sourceId
 * @property {number} sourceTypeId
 * @property {string} targetId
 * @property {number} targetTypeId
 * @property {string?} additionalText
 * @property {number?} textRotation
 * @property {string?} textOrientation
 */

/**
 * @typedef {Object} NormalConnection
 * @property {string} sourceId
 * @property {number} sourceTypeId
 * @property {string} sourceDeviceId
 * @property {string} targetId
 * @property {number} targetTypeId
 * @property {string} targetDeviceId
 */

/**
 * @typedef {Object} ConnectionType
 * @property {number} id
 * @property {string} type
 */

/**
 * @typedef {Object} Label
 * @property {number} relativeX
 * @property {number} relativeY
 * @property {string} displayName
 * @property {string} orientation
 * @property {string} background
 */

/**
 * @typedef {Object} Bus
 * @property {string} id
 * @property {string} busName
 * @property {string} busType
 * @property {BusDevice[]} devices
 */

/**
 * @typedef {Object} BusDevice
 * @property {string} deviceID
 * @property {string} displayText
 * @property {string} imageUrl
 */

/**
 * @typedef {Object} BusPage
 * @property {string} name
 * @property {BusDevice[]} devices
 * @property {Bus} original
 */

/**
 * @typedef {Object} Resistor
 * @property {number} cableID1
 * @property {number} cableID2
 * @property {string} text
 */

const PINOUT_PAGENUM_PREFIX = "C-";

/**
 *
 * @param {number} number
 * @param {d3.Selection} cont
 * @param {QuotePinout | QuoteCableList} parent
 */
function PinoutAddPageNumber(number, cont, parent) {
    cont.append('text')
        .attr('class', 'pinout-pdf-page-num')
        .attr("x", parent.width - parent.margin) // bottom right on page
        .attr("y", parent.height - parent.margin)
        .attr("text-anchor", "end")
        .attr("font-size", 10)
        .attr("data-pdf-x", parent.width) // bottom right in pdf
        .attr("data-pdf-y", 0)
        .attr("data-pdf-transform", `rotate(-90 ${parent.width} 0)`)
        .attr("data-pdf-font-weight", "bold")
        .text(PINOUT_PAGENUM_PREFIX + number);
}

class PinoutCableLines {
    constructor() {
        /**
         * @type {PinoutCableLine[]}
         */
        this.lines = [];
    }
    /**
     *
     * @param {PinoutCableSegment} segment
     * @returns {number}
     */
    placeSegment(segment) {
        let i = 0;
        for (const line of this.lines) {
            if (line.addSegment(segment)) {
                return i;
            }
            i++;
        }

        const line = new PinoutCableLine();
        line.addSegment(segment);
        this.lines.push(line);
        return this.lines.length - 1;
    }

    heightSum() {
        return this.lines.reduce((prev, curr) => prev + curr.height, 0);
    }

    heightSumUntilY_TOP(yIdx) {
        let sum = 0;
        for (let i = this.lines.length - 1; i > yIdx; i--) {
            sum += this.lines[i].height;
        }
        return sum;
    }

}
class PinoutCableLine {
    constructor() {
        /**
         * @type {PinoutCableSegment[]}
         */
        this.segments = [];
        this.height = 0;
    }

    /**
     *
     * @param {PinoutCableSegment} segment
     */
    segmentFitsHere(segment) {
        for (const placedSeg of this.segments) {
            if (placedSeg.overlapsWith(segment)) {
                return false;
            }
        }
        return true;
    }
    addSegment(segment) {
        if (this.segmentFitsHere(segment)) {
            this.segments.push(segment);
            return true;
        }
        return false;
    }
}

class PinoutCableSegment {
    /**
     *
     * @param {number} x1
     * @param {number} x2
     * @param {Cable} cable
     */
    constructor(x1, x2, cable) {
        this.x1 = x1;
        this.x2 = x2;
        this.cable = cable;
    }
    /**
     *
     * @param {PinoutCableSegment} s2
     */
    overlapsWith(s2) {
        const s1 = this;
        return this.isPointInsideSegment(s1.x1, s2) ||
            this.isPointInsideSegment(s1.x2, s2) ||
            this.isPointInsideSegment(s2.x1, s1) ||
            this.isPointInsideSegment(s2.x2, s1);
    }
    /**
     *
     * @param {number} x
     * @param {PinoutCableSegment} seg
     */
    isPointInsideSegment(x, seg) {
        return x >= seg.x1 && x <= seg.x2 || x <= seg.x1 && x >= seg.x2;
    }
}

function safeGetBBox(svgElement) {
    let tempDiv = document.createElement('div');
    tempDiv.setAttribute('style', "position:absolute; visibility:hidden; width:0; height:0");
    document.body.appendChild(tempDiv);
    let tempSvg = document.createElementNS("http://www.w3.org/2000/svg", 'svg');
    tempDiv.appendChild(tempSvg);
    let tempEl = svgElement.cloneNode(true);
    tempSvg.appendChild(tempEl);
    let bb = tempEl.getBBox();
    document.body.removeChild(tempDiv);
    return bb;
}

export class QuotePinout {

    /**
     *
     * @param {JQuery<HTMLElement>} container
     * @param {any[]} busItems
     */
    constructor(container, ajaxResult) {
        try {
            ajaxResult = JSON.parse(JSON.stringify(ajaxResult));
            this.validateContainer(container);
            this.validateAjaxData(ajaxResult);
        } catch (err) {
            return console.log(err);
            //            alert(err);
        }
        this.pageNum = 0;
        this.currUUID = 0;
        /**
         * The jQuery selected container where we will place the pinout pages.
         * @type {JQuery<HTMLElement>}
         */
        this.$container = container;
        /**
         * The DOM Element where we will place the pinout pages.
         */
        this.containerNode = this.$container[0];
        /**
         * Buses with their respective devices.
         * @type {Bus[]}
         */
        this.busItems = ajaxResult.buses;
        /**
         * @type {Object.<string, BusWithExtensions>}
         */
        this.treeLines = ajaxResult.tree_lines;
        /**
         * The devices that have pins and can be connected to each other via cables.
         * @type {PinoutDevice[]}
        */
        this.pinoutDevices = ajaxResult.devices.concat(ajaxResult['network_devices']);
        /**
         * Cables that appear on the bottom of the pinouts.
         * @type {Cable[]}
        */
        this.cables = ajaxResult.cables;
        /**
         * Available connection target types (port, cable, etc.).
         * @type {ConnectionType[]}
        */
        this.connectionTypes = ajaxResult.connection_types;
        /**
         * Resistors that appear on the end of two cables.
         * @type {Resistor[]}
         */
        this.resistors = ajaxResult.resistors;
        /**
         * @type {string[]}
         */
        this.pageBreakBefore = ajaxResult.pageBreakBefore || [];
        if (!Array.isArray(this.pageBreakBefore)) {
            console.log("PINOUT_WARNING: pageBreakBefore was received, but it isn't an array. Replacing it with an empty array.");
            this.pageBreakBefore = [];
        }
        /* if (env === "dev" && !ajaxResult.pageBreakBefore && this.pinoutDevices.length >= 4) {
            this.pageBreakBefore.push(this.pinoutDevices[1].id);
            this.pageBreakBefore.push(this.pinoutDevices[3].id);
            console.log('DEBUG WARNING: created a page break before 2nd pinout device, since the following conditions are met: environment is dev, didn\'t receive pageBreakBefore array in ajax result, pinoutDevices.length is greater than or equal to 2')
        } */
        /**
         * We don't set the DPI and Page Size dynamically because it can be set at any time while printing.
         * Since we're working with vector graphics, the printing utility can scale the pages without loss in quality.
         * The width/height numbers only matter for setting the aspect ratio.
         */
        /**
         * Width of a page in pixels (A4 is 11.693 inches wide, DPI is 72 -> 11.693*72 = 842).
         */
        this.width = 842;
        /**
         * Height of a page in pixels (A4 is 8.268 inches tall, DPI is 72 -> 8.268*72 = 595).
         */
        this.height = 595;
        /**
         * Width and height of a square on the grid in pixels.
         * Changing this scales the whole image up or down, since the other variables depend on it.
         */
        this.rasterSize = 8;
        /**
         * Font size parameters.
         */
        this.fontSizes = {
            /**
             * Size of port name texts on devices.
             */
            portName: this.rasterSize * 0.75,
            /**
             * Size of text that's on/after a cable coming out from a port.
             */
            portAdditionalText: this.rasterSize * 0.9,
            /**
             * Size of text that's on/after a cable coming out from a menorah group port.
             */
            menorahAdditionalText: this.rasterSize * 0.65,
            /**
             * Size of text that's on/after a cable coming out from a port on an IT device.
             */
            itAdditionalText: this.rasterSize * 1.2,
            /**
             * Size of a cable's name.
             */
            cableName: this.rasterSize * 1,
            /**
             * Size of a tree bus line's name, that's before the line on a tree bus page (eg. "Line 1").
             */
            busLineText: this.rasterSize * 1.4,
            /**
             * Size of a tree bus item's description text. Eg. "Living Room<br>Telephone".
             */
            busEntity: this.rasterSize * 1.1,
            busEntitySymbol: this.rasterSize * 0.95,
            /**
             * The text that tells the customers that this is not a 100% correct plan.
             */
            safetyText: this.rasterSize,
            itNameText: this.rasterSize * 1.5,
            /**
             * The text under resistors (usually their resistance)
             */
            resistorText: this.rasterSize * 0.9
        }
        /**
         * Margin at the top/bottom/left/right sides of the page.
         */
        this.margin = this.rasterSize * 3;
        /**
         * An extra margin for the first drawn device added to the page side margin.
         */
        this.devExtraMargin = this.rasterSize;
        /**
         * The space reserved for the label text before tree bus lines (eg. "Line 1").
         */
        this.busLineTextWidth = 60;
        /**
         * Number of lines below each other on tree bus pages.
         */
        this.busLinesPerPage_old = 5;
        this.busLinesPerPage = 5;
        /**
         * Width of an entity on a bus.
         */
        this.busEntitySize = this.rasterSize * 5;
        /**
        * Width and height of a bus entity icon
        */
        this.busIconSize = this.rasterSize * 4;
        /**
         * Distance of tree devices.
         */
        this.busDistance = this.rasterSize * 1;

        this.busTextToLineDist = this.rasterSize * 2;
        this.busLineToWireDist = this.rasterSize * 1;
        /**
         * Distance of (non-tree) devices.
         */
        this.deviceDistance = this.rasterSize * 1;
        /**
         * Stroke width of cables
         */
        this.cableStroke = 1;
        /**
         * Vertical distance of cables.
         */
        this.pinoutDevCablesDist = this.rasterSize * 2;
        /**
         * Vertical distance of the uppermost cable and the bottom of the devices.
         */
        this.cableDevSpace = this.rasterSize * 2 /* * 15 */;
        /**
         * The space between cables on a device's left side (where the upper ports connect to bottom cables).
         */
        this.leftCableSpace = this.rasterSize;
        /**
         * The default space between cables coming out of ports and the ports' labels.
         */
        this.defaultPortLabelDistance = this.rasterSize * 1.5;
        /**
         * The length of the short "cable" coming out of a port when it's connected to a plain text.
         */
        this.portPlainTextDistance = this.rasterSize * 2;
        /**
         * The vertical difference of grouped ports' lines.
         */
        this.menorahDistanceIncrementY = this.rasterSize / 2;
        /**
         * The horizontal difference of grouped ports' linse. It depends on the port text variable, and we also add a margin to it.
         */
        this.menorahDistanceIncrementX = this.fontSizes.menorahAdditionalText / 2 + this.rasterSize / 3 - 1.25;
        /**
         * Height of the DIN rail that the devices are placed on.
         */
        this.dinRailHeight = this.rasterSize * 19;

        this.busPages_old = [];

        this.busPages = [];
        /**
         * @type {PinoutDevice[][]}
         */
        this.pinoutDevPages = [];
        /**
         * After calculation, contains which bus is on which page.
         */
        this.busPageNumberings = {};
        /**
         * @type {string[]}
         */
        this.errors = [];
    }

    /**
     * Experimental module to group device functions
     * @param {PinoutDevice} dev
     */
    device(dev) {
        return {
            hasCableConnectionWithPort: portId => {
                return !!dev.connections
                    .find(conn => this.isConnTypeOf(conn, "port", "cable") && this.getIdOfTypeInConn(conn, "port") == portId);
            },
            hasLowerCableConnectionWithPort: portId => {
                return !!dev.connections
                    .find(conn => {
                        if (this.isConnTypeOf(conn, "port", "cable")) {
                            const portIdIsGood = this.getIdOfTypeInConn(conn, "port") == portId
                            const cable = this.getCableById(this.getIdOfTypeInConn(conn, "cable"));
                            return cable && cable.cableOrientation === "down" && portIdIsGood
                        }
                        else {
                            return false;
                        }
                    });
            }
        }
    }

    warn(msg) {
        console.log(`%c [PINOUT_WARN] %c${msg}`, "background: orange; color: white;", "background: white; color: orangered;");
    }

    /**
     *
     * @param {d3.Selection<any>} container
     * @param {string} textOverride
     */
    insertSafetyText(container) {
        container.insert("text")
          .text(ts('q_safety_text',"This is not an actual electrical plan. Please contact your electrician for guidance."))
          .attr('font-size', this.fontSizes.safetyText)
          .attr('x', this.width - this.margin * 2)
          .attr('y', this.margin / 2 + this.fontSizes.safetyText / 2)
          .attr('text-anchor', "end")
          .attr('fill', "lightgrey");
    }

    /**
     *
     * @param {d3.Selection<any>} container
     * @param {string} text
     */
    insertITNameText(container, text) {
        container.insert("text")
          .text(text)
          .attr('font-size', this.fontSizes.itNameText)
          .attr('x', this.width - this.margin * 2)
          .attr('y', this.margin / 2 + this.fontSizes.itNameText / 2)
          .attr('text-anchor', "end")
          .attr('fill', "black");
    }

    /**
     *
     * @param {JQuery<HTMLElement>} container
     */
    validateContainer(container) {
        if (container.length == 0) {
            throw new Error("Container not found.");
        }
    }


    validateAjaxData(ajaxData) {
        /* const fields = ["buses", "devices", "cables", "connection_types", "resistors"]; */
        const fields = {
            "buses": {

            },
            "devices": {
                id: "string",
                imageUrl: "string",
                originalImageWidth: "number",
                originalImageHeight: "number"
            },
            "network_devices": {
                id: "string",
                imageUrl: "string",
                originalImageWidth: "number",
                originalImageHeight: "number"
            },
            "cables": {

            },
            "connection_types": {

            },
            "resistors": {

            },
        }

        for (const field in fields) {
            if (!(field in ajaxData)) {
                throw new Error(`field "${field}" needs to be in ajax data`);
            }
            else if (!Array.isArray(ajaxData[field])) {
                throw new Error(`field "${field}" needs to be an array`);
            }
            for (const elem of ajaxData[field]) {
                for (const innerField of Object.entries(fields[field])) {
                    if (!(innerField[0] in elem)) {
                        throw new Error(`field ${innerField[0]} missing from ${JSON.stringify(elem)}`);
                    }
                    if (typeof elem[innerField[0]] != innerField[1]) {
                        throw new Error(`field ${innerField[0]} needs to be ${innerField[1]} in ${JSON.stringify(elem)}`);
                    }
                }
            }
        }

    }

    validateDevs(devices) {

    }

    getConnTypeById(id) {
        return this.connectionTypes.find(conn => conn.id == id).type;
    }

    getConnIdByKey(key) {
        return this.connectionTypes.find(conn => conn.type == key).id;
    }

    /**
     *
     * @param {Connection} conn
     * @param {string} type1
     * @param {string} type2
     */
    isConnTypeOf(conn, type1, type2) {
        const types = [this.getConnTypeById(conn.sourceTypeId), this.getConnTypeById(conn.targetTypeId)];
        return types.indexOf(type1) != -1 && types.indexOf(type2) != -1;
    }

    isConnTypeOf_C(type1, type2) {
        return conn => this.isConnTypeOf(conn, type1, type2);
    }

    /**
     *
     * @param {Connection} conn
     * @param {string} type
     */
    doesConnHaveType(conn, type) {
        return this.getConnTypeById(conn.sourceTypeId) == type || this.getConnTypeById(conn.targetTypeId) == type;
    }

    /**
     *
     * @param {Connection} conn
     * @param {string} type
     */
    getIdOfTypeInConn(conn, type) {
        if (this.getConnTypeById(conn.sourceTypeId) == type) {
            return conn.sourceId;
        }
        else if (this.getConnTypeById(conn.targetTypeId) == type) {
            return conn.targetId;
        }
        return null;
    }

    getIdOfTypeInConn_C(type) {
        return conn => this.getIdOfTypeInConn(conn, type);

    }

    sortCables() {
        const infinites = [], startEnds = [];
        if (!this.cables || !Array.isArray(this.cables) || this.cables.length == 0) {
            return;
        }
        this.cables.forEach(cable => {
            if (cable.beginningPort || cable.endingPort) {
                startEnds.push(cable);
            }
            else {
                infinites.push(cable);
            }
        });
        this.cables = infinites.concat(startEnds);
    }

    filterMenorahGroups() {
        if (!this.pinoutDevices) {
            return;
        }
        for (const dev of this.pinoutDevices) {
            if (dev.menorahGroups) {
                dev.origiMenorahGroups = [];
                for (let i = 0; i < dev.menorahGroups.length; i++) {
                    var newMg = [];
                    dev.origiMenorahGroups.push(newMg);

                    dev.menorahGroups[i].forEach(elem => {
                        newMg.push(elem);
                    });

                    dev.menorahGroups[i] = dev.menorahGroups[i].filter(portId => {
                        return !!dev.connections.find(conn => this.isConnTypeOf(conn, "port", "plainText") && this.getIdOfTypeInConn(conn, "port") == portId);
                    });
                }
            }
        }
    }

    chunkArray(myArray, chunk_size) {
        let index = 0;
        const arrayLength = myArray.length;
        const tempArray = [];

        for (index = 0; index < arrayLength; index += chunk_size) {
            const myChunk = myArray.slice(index, index + chunk_size);
            tempArray.push(myChunk);
        }

        return tempArray;
    }

    pagesFromBuses() {
        const maxFitWidth = this.width - this.margin * 2 - this.busLineTextWidth + this.busDistance;
        const numDevicesInLine = Math.floor(maxFitWidth / (this.busEntitySize + this.busDistance));
        let lines = [];
        for (const busLine of this.busItems) {
            busLine.devices[0].isFirstItem = true;
            busLine.devices[busLine.devices.length - 1].isLastItem = true;
            for (const chunk of this.chunkArray(busLine.devices, numDevicesInLine)) {
                const isAir = (busLine.busName + "").toLowerCase().split(' ').indexOf('air') !== -1;
                const isTree = typeof busLine.id === "string" && busLine.id.indexOf("TreeLine") === 0;
                lines.push({
                    name: busLine.busName + (isTree ? " (This line is hidden in production)" : ""),
                    devices: chunk,
                    original: busLine,
                    isAir,
                    busWeight: isAir ? 0.5 : 1
                });
            }
            lines[lines.length - 1].busWeight = 1;
        }
        let currLines = [];
        const pages = [{ lines: currLines }];
        let pageSeenLines = [];
        let currWeight = 0;
        for (let i = 0; i < lines.length; i++) {
            if (Math.ceil(currWeight) >= this.busLinesPerPage) {
                currLines = [];
                pages.push({ lines: currLines });
                pageSeenLines = [];
                currWeight = 0;
            }

            let line = lines[i];
            currWeight += line.busWeight;
            currLines.push(line);

            if (pageSeenLines.find(x => x == line.original)) {
                line.name = "";
            }
            else {
                pageSeenLines.push(line.original);
            }

        }
        return pages;
    }

    pagesFromTreeBuses() {
        const availableWidth = this.width - this.margin * 2 - this.busLineTextWidth -
            this.busLineToWireDist - this.busTextToLineDist + this.busDistance;
        const numDevicesInLine = Math.floor(availableWidth / (this.busEntitySize + this.busDistance));
        let lines = [];
        for (const treeBusIdx in this.treeLines) {
            const treeBus = this.treeLines[treeBusIdx];
            treeBus.extensions.forEach(extension => {
                extension.lines.forEach(line => {
                    let lineChunks = [];
                    line.wires.forEach(wire => {
                        const chunks = this.chunkArray(wire.devices, numDevicesInLine).map((chunk, idx, arr) => {
                            return {
                                busId: treeBusIdx,
                                original: line,
                                name: line.name,
                                extImg: extension.image,
                                extName: extension.name,
                                devices: chunk,
                                wireType: wire.type,
                                even: idx % 2 == 0,
                                directConnectToLine: idx == 0,
                                wireConnectToPrev: idx != 0,
                                wireConnectToNext: idx + 1 < arr.length,
                                continueToNext: false, // default val
                                continueFromPrev: false, // default val
                                firstOfLine: undefined
                            };
                        });
                        lines = lines.concat(chunks);
                        lineChunks = lineChunks.concat(chunks);
                    });
                    /* console.log(lineChunks); */
                    let lastDirect;
                    for (let i = lineChunks.length - 1; i >= 0; i--) {
                        if (lineChunks[i].directConnectToLine) {
                            lastDirect = i;
                            break;
                        }
                    }
                    if (lastDirect) {
                        for (let i = 0; i < lastDirect; i++) {
                            if (lineChunks[i]) {
                                lineChunks[i].continueToNext = true;
                            }
                            if (lineChunks[i + 1]) {
                                lineChunks[i + 1].continueFromPrev = true;
                            }
                        }
                    }
                    if (lineChunks[0]) {
                        lineChunks[0].firstOfLine = true;
                    }
                    for (let i = 1; i < lineChunks.length; i++) {
                        lineChunks[i].extImg = "";
                        lineChunks[i].firstOfLine = false;
                    }
                });
            });
        }
        const pages = [];
        for (let i = 0; i < lines.length; i += this.busLinesPerPage) {
            const pageSeenLines = [];
            const pageLines = lines.slice(i, i + this.busLinesPerPage);
            for (const line of pageLines) {
                if (pageSeenLines.find(x => x == line.original)) {
                    line.name = "";
                }
                else {
                    pageSeenLines.push(line.original);
                }
            }
            pages.push({ lines: pageLines })
        }
        /* console.log(pages); */
        return pages;
    }

    /**
     *
     * @param {d3.Selection<any>} svg
     * @returns {d3.Selection<any>}
     */
    appendDefsToSVG(svg) {
        svg.append('defs')
            .append('marker')
            .attr('id', 'head')
            .attr('orient', 'auto')
            .attr('markerWidth', '4')
            .attr('markerHeight', '4')
            .attr('refX', '0.1')
            .attr('refY', '2')
            .append('path')
            .attr('d', 'M0,0 V4 L4,2 Z')
            .attr('fill', 'black')
        return svg;
    }

    /**
     *
     * @param {d3.Selection} cont
     * @param {*} text
     * @param {*} size
     * @param {*} x
     * @param {*} y
     * @param {*} rot
     * @param {*} anchor
     * @param {*} maxWidth
     */
    drawText(cont, text, size, x, y, rot, anchor = 'start', maxWidth = null) {
        const textElem = cont.append('text')
          .attr('font-size', size)
          .attr('text-anchor', anchor)
          .attr('x', x)
          .attr('y', y)
          .attr('transform', `rotate(${rot} ${x} ${y})`);
        if (maxWidth) {
            var isUnderlined = false;
            if (text.split(' ').length === 1 && text.indexOf('_') !== -1) {
                isUnderlined = true;
                text = text.split('_').join(' ');
            }
            var parts = SVG_TEXT_HELPER.wrapLabel(text, maxWidth, size);
            parts.forEach(function (part, idx) {
                if (isUnderlined) {
                    part = part.split(' ').join('_');
                }
                textElem.append('tspan')
                  .attr('x', x)
                  .attr('dy', idx === 0 ? 0 : size)
                  .text(part);
            });
        }
        else {
            textElem.text(text);
        }
        return textElem;
    }

    /**
     *
     * @param {any} pageObj
     * @param {d3.Selection} cont
     */
    renderBusPage(pageObj, cont) {
        /* console.log(pageObj, cont); */
        const availableHeight = this.height - this.margin * 2;
        const firstDevPosX = this.margin + this.busLineTextWidth + this.busTextToLineDist + this.busLineToWireDist;
        pageObj.lines.forEach((line, lineIdx) => {
            const yPos = this.margin + availableHeight / this.busLinesPerPage * lineIdx;
            const centerY = yPos + this.busEntitySize / 2;
            const linePlusMarginRight = { x: this.margin + this.busLineTextWidth - 5, y: yPos + this.busEntitySize / 2 + this.fontSizes.busLineText / 2 * 0.7 };

            if (line.name.length) {
                const imgSize = 40;
                const extNamePos = { x: linePlusMarginRight.x - imgSize - 3, y: centerY - imgSize / 2 };

                // if this is the absolute first occurrence of the line, and its extensions has an image
                if (line.extImg && line.extImg.length) {
                    // draw the image
                    cont.append('svg:image')
                      .attr('x', linePlusMarginRight.x - imgSize)
                      .attr('y', centerY - imgSize / 2)
                      .attr('xlink:href', `${getFrontendURL()}/${line.extImg}`)
                      .attr('width', imgSize)
                      .attr('height', imgSize);
                    // draw the line name
                    cont.append('text')
                      .attr('text-anchor', 'middle')
                      .attr('font-size', this.fontSizes.busLineText)
                      .attr('x', linePlusMarginRight.x - imgSize / 2)
                      .attr('y', centerY + imgSize / 2 + this.fontSizes.busLineText + 2)
                      .text(line.name);
                }
                else {
                    // draw the line name
                    cont.append('text')
                      .attr('x', linePlusMarginRight.x)
                      .attr('y', linePlusMarginRight.y)
                      .attr('text-anchor', 'end')
                      .attr('font-size', this.fontSizes.busLineText)
                      .attr('fill', 'black')
                      .text(line.name);
                }
                if (line.firstOfLine) {
                    // draw the extension name
                    cont.append('text')
                      .attr('x', extNamePos.x)
                      .attr('y', extNamePos.y)
                      .attr('transform', `rotate(-90 ${extNamePos.x} ${extNamePos.y})`)
                      .attr('text-anchor', 'end')
                      .attr('font-size', this.fontSizes.busLineText)
                      .attr('fill', 'black')
                      .text(line.extName);
                }
            }

            // draw direct connection
            if (line.directConnectToLine) {
                if (line.name.length && !line.continueFromPrev || lineIdx != 0) {
                    // draw additional connector line
                    cont.insert('line', ':first-child')
                      .attr('stroke', 'black')
                      .attr('stroke-width', this.cableStroke)
                      .attr('x1', linePlusMarginRight.x + 5)
                      .attr('y1', centerY)
                      .attr('x2', this.margin + this.busLineTextWidth + this.busTextToLineDist)
                      .attr('y2', centerY);

                }

                // draw direct connection circle
                cont.append('circle')
                  .attr('cx', linePlusMarginRight.x + 5)
                  .attr('cy', centerY)
                  .attr('r', this.cableStroke * 1.5)
                  .attr('fill', 'white')
                  .attr('stroke', 'black')
                  .attr('stroke-width', this.cableStroke);
            }
            // draw wire type
            if (line.directConnectToLine) {
                if (line.wireType && line.wireType.length) {
                    const lastDevX = firstDevPosX + line.devices.length * (this.busEntitySize + this.busDistance);
                    const wireTextPos = { x: (firstDevPosX + lastDevX) / 2, y: centerY - 2 };
                    var drawnText = this.drawText(cont, line.wireType, this.fontSizes.busEntity, wireTextPos.x, wireTextPos.y - this.busIconSize / 2 - 1, 0, 'middle');
                    try {
                        const minLeft = linePlusMarginRight.x + 10;
                        var bbox = drawnText.node().getBBox();
                        if (bbox.x < minLeft) {
                            /* console.log('adjusted left', bbox.x, minLeft) */
                            drawnText.attr('text-anchor', 'start');
                            drawnText.attr('x', minLeft);
                            drawnText.select('tspan').attr('x', minLeft);
                        }
                    } catch (err) {
                        console.error('BUG: error adjusting tree cable text', err);
                    }
                }
            }

            // draw line for continuing input
            if (line.continueFromPrev) {
                const lineBusCableX = this.margin + this.busLineTextWidth;
                const prevY = lineIdx == 0 ? centerY - this.busEntitySize / 2 : centerY - availableHeight / this.busLinesPerPage;
                cont.insert('line', ':first-child')
                  .attr('stroke', 'black')
                  .attr('stroke-width', this.cableStroke)
                  .attr('x1', lineBusCableX)
                  .attr('y1', centerY)
                  .attr('x2', lineBusCableX)
                  .attr('y2', prevY);
                if (line.directConnectToLine) {
                    cont.insert('line', ':first-child')
                      .attr('stroke', 'black')
                      .attr('stroke-width', this.cableStroke)
                      .attr('x1', lineBusCableX)
                      .attr('y1', centerY)
                      .attr('x2', lineBusCableX + this.busTextToLineDist)
                      .attr('y2', centerY);
                }
            }

            if (lineIdx == pageObj.lines.length - 1 && line.continueToNext) {
                const lineBusCableX = linePlusMarginRight.x + 5;
                cont.insert('line', ':first-child')
                  .attr('stroke', 'black')
                  .attr('stroke-width', this.cableStroke)
                  .attr('x1', lineBusCableX)
                  .attr('y1', centerY)
                  .attr('x2', lineBusCableX)
                  .attr('y2', centerY + availableHeight / this.busLinesPerPage - this.busEntitySize / 2);
            }

            // insert direct connection from line
            if (line.directConnectToLine) {
                cont.insert('line', ':first-child')
                  .attr('stroke', 'black')
                  .attr('stroke-width', this.cableStroke)
                  .attr('x1', this.margin + this.busLineTextWidth + this.busTextToLineDist)
                  .attr('y1', centerY)
                  .attr('x2', this.margin + this.busLineTextWidth + this.busTextToLineDist + this.busLineToWireDist)
                  .attr('y2', centerY);
            }

            // connect end of line to above beginning of next
            if (line.wireConnectToNext) {
                const lastDevX = firstDevPosX + (line.devices.length) * (this.busEntitySize + this.busDistance) - this.busDistance;
                const pts = [
                    [lastDevX, centerY],
                    [lastDevX + this.busDistance, centerY], //last dev center to end
                    [lastDevX + this.busDistance, centerY + availableHeight / this.busLinesPerPage - this.busIconSize], // end to next line end
                    [firstDevPosX - this.busLineToWireDist, centerY + availableHeight / this.busLinesPerPage - this.busIconSize] // next line end to next line begin
                ];

                if (lineIdx == pageObj.lines.length - 1) {
                    pts.push([firstDevPosX - this.busLineToWireDist, centerY + availableHeight / this.busLinesPerPage - this.busEntitySize / 2]) // if just starting page, draw from begin to first dev
                }

                // insert points
                for (var i = 0; i < pts.length - 1; i++) {
                    cont.insert('line', ':first-child')
                      .attr('stroke', 'black')
                      .attr('stroke-width', this.cableStroke)
                      .attr('x1', pts[i][0])
                      .attr('y1', pts[i][1])
                      .attr('x2', pts[i + 1][0])
                      .attr('y2', pts[i + 1][1]);
                }
            }
            // connect beginning of line to previous connection point
            if (line.wireConnectToPrev) {
                // above line start
                // line start
                const pts = [
                    [firstDevPosX - this.busLineToWireDist, centerY - (lineIdx == 0 ? this.busIconSize / 2 : this.busIconSize)], // if just starting page, let line be shorter
                    [firstDevPosX - this.busLineToWireDist, centerY],
                    [firstDevPosX, centerY],
                ];
                for (var i = 0; i < pts.length - 1; i++) {
                    cont.insert('line', ':first-child')
                      .attr('stroke', 'black')
                      .attr('stroke-width', this.cableStroke)
                      .attr('x1', pts[i][0])
                      .attr('y1', pts[i][1])
                      .attr('x2', pts[i + 1][0])
                      .attr('y2', pts[i + 1][1]);
                }
            }

            // draw devices
            line.devices.forEach((dev, devIdx) => {
                const xPos = firstDevPosX + devIdx * (this.busEntitySize + this.busDistance);
                const center = { x: xPos + this.busEntitySize / 2, y: yPos + this.busEntitySize / 2 };
                // draw device icon
                cont.append('svg:image')
                  .attr('x', center.x - this.busIconSize / 2)
                  .attr('y', center.y - this.busIconSize / 2)
                  .attr('xlink:href', `${getFrontendURL()}/${dev.image}`)
                  .attr('width', this.busIconSize)
                  .attr('height', this.busIconSize);

                // draw device text
                const devFontSize = dev.text.indexOf("_") == -1 ? this.fontSizes.busEntity : this.fontSizes.busEntitySymbol;
                this.drawText(cont, dev.text, devFontSize, center.x, center.y + this.busIconSize / 2 + devFontSize + 1, 0, 'middle', this.busEntitySize);

                if (devIdx == 0) {
                    // connect to begin
                    cont.insert('line', ':first-child')
                      .attr('stroke', 'black')
                      .attr('stroke-width', this.cableStroke)
                      .attr('x1', center.x)
                      .attr('y1', center.y)
                      .attr('x2', center.x - this.busEntitySize / 2)
                      .attr('y2', center.y);
                }
                if (devIdx < line.devices.length - 1) {
                    // connect to next
                    cont.insert('line', ':first-child')
                      .attr('stroke', 'black')
                      .attr('stroke-width', this.cableStroke)
                      .attr('x1', center.x)
                      .attr('y1', center.y)
                      .attr('x2', center.x + this.busEntitySize + this.busDistance)
                      .attr('y2', center.y);
                }
                else if (line.wireConnectToNext) {
                    cont.insert('line', ':first-child')
                      .attr('stroke', 'black')
                      .attr('stroke-width', this.cableStroke)
                      .attr('x1', center.x)
                      .attr('y1', center.y)
                      .attr('x2', center.x + this.busEntitySize / 2)
                      .attr('y2', center.y);
                }
            });
        });
    }

    treeToContainer() {
        for (let i = 0; i < this.busPages.length; i++) {
            const page = this.busPages[i];
            let cont = d3.select(this.containerNode).append("svg")
                .attr("width", '100%')
                .attr("height", '800')
                .attr("viewBox", `0 0 ${this.width} ${this.height}`)
                .attr("class", "pinout lightshadow")
                .append("g");
            this.appendDefsToSVG(cont);
            this.insertSafetyText(cont);
            this.addPageNumber(page.pageNum, cont);

            this.renderBusPage(page, cont);
        }
    }

    busesToContainer() {
        const BUS_TEXT_POS = {
            x: this.margin,
            y: this.margin + this.rasterSize * 2
        };
        const LINE_TEXT_X = {
            x1: this.margin,
            x2: this.margin + this.busLineTextWidth
        };

        const LINE_Y_OFFSET = BUS_TEXT_POS.y + this.rasterSize * 1.5;

        const CONNECT_LENGTH = this.rasterSize * 1.5;

        for (let i = 0; i < this.busPages_old.length; i++) {
            const page = this.busPages_old[i];
            if (Array.isArray(page.lines) && page.lines.length === 0) {
                continue;
            }
            let cont = d3.select(this.containerNode).append("svg")
                .attr("width", '100%')
                .attr("height", '800')
                .attr("viewBox", `0 0 ${this.width} ${this.height}`)
                .attr("class", "pinout lightshadow")
                .append("g");

            this.appendDefsToSVG(cont);
            this.insertSafetyText(cont);
            this.addPageNumber(page.pageNum, cont);

            let plusOffset = 0;

            for (let i = 0; i < page.lines.length; i++) {
                /* const Y = LINE_Y_OFFSET + i * (this.height - LINE_Y_OFFSET - this.margin) / this.busLinesPerPage; */
                this.renderBusLine(cont, LINE_TEXT_X, LINE_Y_OFFSET + plusOffset, page.lines[i], CONNECT_LENGTH);
                plusOffset += (this.height - LINE_Y_OFFSET - this.margin) / this.busLinesPerPage * page.lines[i].busWeight;
            }
        }
    }

    renderBusLine(cont, LINE_TEXT_X, BUS_LINE_1_Y, line, CONNECT_LENGTH) {
        const lineText = cont.append("text")
            .text(line.name)
            .attr("x", LINE_TEXT_X.x1)
            .attr("y", BUS_LINE_1_Y - this.fontSizes.busLineText / 2)
            .attr("font-size", this.fontSizes.busLineText);

        const isAir = (line.original.busName + "").toLowerCase().split(' ').indexOf('air') !== -1;
        const dashArray = isAir ? (this.cableStroke + " " + this.cableStroke) : "";
        const cDashArray = isAir ? (this.cableStroke + " " + this.cableStroke * 0.75) : "";

        let busLine;
        if (!isAir) {
            busLine = cont.append("line")
                .attr("stroke", "black")
                .attr("stroke-width", this.cableStroke)
                .attr("stroke-dasharray", dashArray)
                .attr("x1", LINE_TEXT_X.x2)
                .attr("y1", BUS_LINE_1_Y)
                .attr("x2", this.width - this.margin)
                .attr("y2", BUS_LINE_1_Y);
        }
        else {

            const lineBBox = lineText.node().getBBox();
            const imgSize = lineBBox.height * 1.2
            const centerY = lineBBox.y + lineBBox.height / 2;
            cont.append("svg:image")
                .attr("height", imgSize)
                .attr("width", imgSize)
                .attr("x", lineBBox.x + lineBBox.width + 5)
                .attr("y", centerY - imgSize / 2)
                .attr("xlink:href", `${getFrontendURL()}/images/pinout_pictures/air.png`)
        }

        // add items
        for (let j = 0; j < line.devices.length; j++) {
            const item = line.devices[j];
            let nextX = this.margin + this.busLineTextWidth + j * (this.busEntitySize + this.busDistance);
            let iconY = BUS_LINE_1_Y + CONNECT_LENGTH;

            if (!isAir) {
                //line from line to item
                cont.append("line")
                    .attr("stroke", "black")
                    .attr("stroke-width", this.cableStroke)
                    .attr("stroke-dasharray", dashArray)
                    .attr("x1", nextX + this.busEntitySize / 2)
                    .attr("y1", BUS_LINE_1_Y + 1)
                    .attr("x2", nextX + this.busEntitySize / 2)
                    .attr("y2", iconY);
                cont.append("circle")
                    .attr("cx", nextX + this.busEntitySize / 2)
                    .attr("cy", BUS_LINE_1_Y)
                    .attr("r", 2)
                    .attr("fill", isAir ? "white" : "black")
                    .attr("stroke", isAir ? "black" : "")
                    .attr("stroke-width", isAir ? this.cableStroke : "")
                    .attr("stroke-dasharray", cDashArray);
                if (item.isFirstItem) {
                    busLine.attr("x1", nextX + this.busEntitySize / 2);
                }
                if (item.isLastItem) {
                    busLine.attr("x2", nextX + this.busEntitySize / 2);
                }
            }

            // item
            cont.append("svg:image")
                .attr("xlink:href", `${getFrontendURL()}/${item.imageUrl}`)
                .attr("data-deviceId", item.deviceID)
                .attr("width", this.busEntitySize)
                .attr("height", this.busEntitySize)
                .attr("x", nextX)
                .attr("y", iconY);
            // item text
            this.drawText(cont, item.displayText, this.fontSizes.busEntitySymbol,
                nextX + this.busEntitySize / 2, iconY + this.fontSizes.busEntitySymbol + this.busEntitySize + 1,
                0, 'middle', this.busEntitySize);


        }
    }

    /**
     *
     * @param {PinoutDevice} dev
     */
    getDevUpperPorts(dev) {
        return dev.ports.filter(port => port.portOrientation == "up");
    }

    getCableById(id) {
        return this.cables.find(c => c.cableID === id);
    }

    /**
     *
     * @param {PinoutDevice} dev
     */
    numberOfDevUpDownConnections(dev) {
        let n = 0;
        const usedPorts = {};
        for (const conn of dev.connections) {
            if (this.isConnTypeOf_C("port", "cable")(conn)) {
                const portId = this.getIdOfTypeInConn_C("port")(conn);
                const port = dev.ports.find(port => port.portID == portId);
                const cableId = this.getIdOfTypeInConn_C("cable")(conn);
                const cable = this.getCableById(cableId);
                if (port && cable && cable.cableOrientation === "down" && port.portOrientation == "up" && !usedPorts[portId]) {
                    usedPorts[portId] = true;
                    n++;
                }
            }
        }
        return n;
    }

    /**
     *
     * @param {PinoutDevice} dev
     */
    devLeftCablesSpace(dev) {
        return this.numberOfDevUpDownConnections(dev) * this.leftCableSpace;
    }

    placeDevsOnRail() {
        if (!this.pinoutDevices) {
            return;
        }
        for (let dev of this.pinoutDevices) {
            if (!("railTop" in dev)) {
                dev.railTop = 0;
            }
            if (!("railBot" in dev)) {
                dev.railBot = dev.originalImageHeight;
            }

            const devRailSize = dev.railBot - dev.railTop;
            let scale = this.dinRailHeight / devRailSize;

            const avblSpace = this.width - this.margin * 2;
            if (scale * dev.originalImageWidth > avblSpace) {
                scale = avblSpace / dev.originalImageWidth;
            }

            dev.height = scale * dev.originalImageHeight;
            dev.width = scale * dev.originalImageWidth;
            dev.yOffset = scale * -dev.railTop;
            dev.calc_width = dev.width;
            dev.ports.forEach(port => {
                port.xPosition *= scale;
                port.yPosition *= scale;
                if (port.label) {
                    if (!("relativeX" in port.label)) {
                        port.label.relativeX = 0;
                    }
                    if (!("relativeY" in port.label)) {
                        port.label.relativeY = port.portOrientation == "up" ? this.defaultPortLabelDistance : -this.defaultPortLabelDistance;
                    }
                    if (!("background" in port.label)) {
                        port.label.background = "#ffffff";
                    }
                    if (!("orientation" in port.label)) {
                        port.label.orientation = "vertical";
                    }

                    port.label.relativeX *= scale;
                    port.label.relativeY *= scale;
                }
            });

            let upperPortIdx = 0;
            for (const port of dev.ports) {
                port.xOriginPosition = port.xPosition;
                port.yOriginPosition = port.yPosition;

                if (port.portOrientation == "up" && this.device(dev).hasLowerCableConnectionWithPort(port.portID)) {
                    port.xPosition = port.yPosition = ++upperPortIdx * -this.leftCableSpace;
                }
            }

            this.sortMenorahGroups();

            const devMargin = this.getDeviceMargin(dev);
            dev.marginLeft = devMargin.left;
            dev.marginRight = devMargin.right;
            dev.calc_width += dev.marginLeft + dev.marginRight;
        }
    }

    /**
     *
     * @param {PinoutDevice} device
     * @param {string} portId
     * @returns {Port}
     */
    getPortOfDevice(device, portId) {
        if (!device) { return null; }
        return device.ports.find(port => port.portID == portId);
    }

    /**
     *
     * @param {number} deviceID
     * @returns {Device}
     */
    getPinoutDeviceById(deviceID) {
        return this.pinoutDevices.find(dev => dev.id == deviceID);
    }

    /**
     *
     */
    sortMenorahGroups() {
        this.pinoutDevices.forEach(dev => {
            if (Array.isArray(dev.menorahGroups)) {
                dev.menorahGroups.forEach(group => this.sortMenorahGroup(dev, group));
            }
        });
    }

    /**
     *
     * @param {PinoutDevice} dev
     * @param {string[]} group
     */
    sortMenorahGroup(dev, group) {
        if (Array.isArray(group)) {
            group.sort((a, b) => {
                const portA = this.getPortOfDevice(dev, a);
                const portB = this.getPortOfDevice(dev, b);
                if (portA.xOriginPosition < portB.xOriginPosition) {
                    return -1;
                }
                else {
                    return 1;
                }
            });
        }
    }

    transformPorts() {
        if (!this.pinoutDevices) {
            return;
        }
        for (const dev of this.pinoutDevices) {
            dev.ports = [];
            if (dev.inputs && Array.isArray(dev.inputs)) {
                dev.ports = dev.ports.concat(dev.inputs.map(input => {
                    input.input = true;
                    return input;
                }));
            }

            if (dev.outputs && Array.isArray(dev.outputs)) {
                dev.ports = dev.ports.concat(dev.outputs.map(output => {
                    output.input = false;
                    return output;
                }));
            }
            if (!Array.isArray(dev.connections)) {
                dev.connections = [];
            }
        }

        if (Array.isArray(this.cables)) {
            for (const cable of this.cables) {
                cable.connectedPorts = [];
                for (const dev of this.pinoutDevices) {
                    for (const conn of dev.connections) {
                        if (this.isConnTypeOf(conn, "cable", "port") && this.getIdOfTypeInConn(conn, "cable") === cable.cableID) {
                            cable.connectedPorts.push(this.getPortOfDevice(dev, this.getIdOfTypeInConn(conn, "port")));
                        }
                    }
                }
                if (!('cableOrientation' in cable)) {
                    cable.cableOrientation = cable.connectedPorts.every(port => port.portOrientation === "up") && cable.connectedPorts.length >= 2 ? "up" : "down";
                }
            }
        }
    }

    pagesFromPinoutDevs() {
        this.transformPorts();
        this.placeDevsOnRail();
        const maxFitWidth = this.width - this.margin * 2;
        let currentCapacity = 0;
        const pages = [];
        let currentPage = [];
        pages.push(currentPage);

        for (let i = 0; i < this.pinoutDevices.length; i++) {
            const dev = this.pinoutDevices[i];
            const totalWidth = dev.calc_width + this.deviceDistance;
            if (currentPage.length === 0 || currentCapacity + totalWidth <= maxFitWidth && !_does(this.pageBreakBefore).contain(dev.id)) {
                currentPage.push(dev);
                currentCapacity += totalWidth;
            } else {
                currentCapacity = totalWidth;
                currentPage = [dev];
                pages.push(currentPage);
            }
        }
        if (pages.length == 1 && currentPage.length == 0) {
            return [];
        }
        return pages;
    }

    /**
     *
     * @param {d3.Selection} cont
     */
    addPageNumber(number, cont) {
        PinoutAddPageNumber(number, cont, this);
    }

    /**
     *
     * @param {PinoutDevice} device
     * @param {string} portID
     */
    getMenorahGroup(device, portID) {
        if (Array.isArray(device.menorahGroups)) {
            const grp = device.menorahGroups.find(group => group.indexOf(portID) != -1);
            if (Array.isArray(grp) && grp.length > 0) {
                return grp;
            }
            else {
                return null;
            }
        }
        else {
            return null;
        }
    }

    isPortNameDisplayed(device, portID) {
        if (Array.isArray(device.origiMenorahGroups)) {
            const group = device.origiMenorahGroups.find(group => group.indexOf(portID) !== -1);
            return !group;
        }
        return true;
    }

    getPortMenorahBound(device, portId, menorahGroup) {
        const port = this.getPortOfDevice(device, portId);
        if (!device || !port) {
            return 0;
        }
        const menorahIdx = this.getMenorahIdx(menorahGroup, portId);
        return port.xOriginPosition + menorahIdx * this.menorahDistanceIncrementX;
    }

    /**
     *
     * @param {PinoutDevice} device
     * @param {string[]} menorahGroup
     */
    getMenorahSegment(device, menorahGroup) {
        if (device && Array.isArray(menorahGroup) && menorahGroup.length > 0) {
            const x1 = this.getPortMenorahBound(device, menorahGroup[0], menorahGroup);
            const x2 = this.getPortMenorahBound(device, menorahGroup[menorahGroup.length - 1], menorahGroup);
            return {
                x1,
                x2,
                leftSpace: x1,
                rightSpace: x2 - device.width
            };
        }
        else {
            return undefined;
        }
    }

    /**
     *
     * @param {PinoutDevice} device
     */
    getDeviceMenorahSegments(device) {
        if (!device.menorahGroups) {
            return [];
        }
        return device.menorahGroups
            .map(group => this.getMenorahSegment(device, group))
            .filter(segment => !!segment);
    }

    /**
     *
     * @param {PinoutDevice} device
     */
    getDeviceMenorahBounds(device) {
        try {
            const segments = this.getDeviceMenorahSegments(device);
            let left = 0, right = 0;
            for (const segment of segments) {
                if (segment.leftSpace < left) {
                    left = segment.leftSpace;
                }
                if (segment.rightSpace > right) {
                    right = segment.rightSpace;
                }
            }
            left = -left;
            return { left, right };
        }
        catch (e) {
            return { left: 0, right: 0 };
        }
    }

    /**
     *
     * @param {PinoutDevice} device
     */
    getDeviceMargin(device) {
        const bounds = this.getDeviceMenorahBounds(device);
        const leftCableSpace = this.devLeftCablesSpace(device);
        if (leftCableSpace > bounds.left) {
            bounds.left = leftCableSpace;
        }
        return bounds;
    }

    /**
     *
     * @param {string[]} menorahGroup
     * @param {string} portId
     */
    getMenorahIdx(menorahGroup, portId) {
        if (Array.isArray(menorahGroup) && menorahGroup.length > 0) {
            const groupCenter = (menorahGroup.length + 1) / 2;
            const devIdx = menorahGroup.findIndex(port => port == portId) + 1;
            if (devIdx == 0) {
                return undefined;
            }
            return devIdx - groupCenter;
        }
        else {
            return undefined;
        }
    }

    /**
     *
     * @param {string[]} menorahGroup
     * @param {string} portId
     */
    getMenorahLineColor(menorahGroup, portId) {
        if (Array.isArray(menorahGroup) && menorahGroup.length > 0) {
            const devIdx = menorahGroup.findIndex(port => port == portId);
            if (devIdx == -1) {
                return undefined;
            }
            return devIdx % 2 == 0 ? "black" : "grey";
        }
        else {
            return undefined;
        }
    }

    /**
     *
     * @param {PinoutDevice} device
     * @param {string[]} menorahGroup
     */
    getMenorahGroupBaseY(device, menorahGroup) {
        if (Array.isArray(menorahGroup) && menorahGroup.length > 0) {
            return menorahGroup.map(portId => this.getPortOfDevice(device, portId)).sort((a, b) => {
                return a.yOriginPosition < b.yOriginPosition ? -1 : 1;
            })[0].yOriginPosition;
        }
        else {
            return 0;
        }
    }

    /**
     *
     * @param {Cable} cable
     */
    cableEnd(cable) {
        if (cable.endingPort) {
            const dev = this.getPinoutDeviceById(cable.endingPort.deviceId);
            const port = this.getPortOfDevice(dev, cable.endingPort.portId);
            return {
                device: dev,
                port: port
            };
        }
        return null;
    }

    /**
     *
     * @param {Resistor} resistor
     * @param {Cable[]} cables
     */
    findResistorCables(resistor, cables, devices) {
        const cable1 = cables.find(cable => cable.cableID == resistor.cableID1);
        const cable2 = cables.find(cable => cable.cableID == resistor.cableID2);

        if (cable1 && cable2) {
            const cable1DevPort = this.cableEnd(cable1);
            const cable2DevPort = this.cableEnd(cable2);
            if (cable1DevPort && cable2DevPort && devices.find(dev => dev == cable1DevPort.device) && devices.find(dev => dev == cable2DevPort.device)) {
                return { cable1, cable1DevPort, cable2, cable2DevPort };
            }
        }
        return null;
    }

    drawRect(container, x1, x2, centerY, height, fill, stroke, strokeWidth, widthMinus) {
        let startX, width;
        if (x1 < x2) {
            startX = x1;
            width = x2 - x1;
        }
        else {
            startX = x2;
            width = x1 - x2;
        }
        startX += widthMinus;
        width -= widthMinus * 2;
        const realY = centerY - height / 2;
        container.append("rect")
          .attr('x', startX)
          .attr('y', realY)
          .attr('width', width)
          .attr('height', height)
          .attr('fill', fill)
          .attr('stroke', stroke)
          .attr('stroke-width', strokeWidth);
    }

    /**
     *
     * @param {PinoutDevice[]} items
     */
    getLowestDeviceY(items) {
        if (items.length == 0) {
            return undefined;
        }
        let maxY = items[0].bottomY;
        for (let i = 1; i < items.length; i++) {
            if (items[i].bottomY > maxY) {
                maxY = items[i].bottomY;
            }
        }
        return maxY;
    }

    /**
     *
     * @param {PinoutDevice[]} items
     */
    getHighestDeviceY(items) {
        if (items.length == 0) {
            return undefined;
        }

        let minY = Number.POSITIVE_INFINITY;
        for (let i = 0; i < items.length; i++) {
            const itemSeenConns = {}; // REMOVE IF DISTINCT SELECT NOT NEEDED
            const devDrawnY = items[i].drawnY;
            const topExtraSpace = items[i].connections.filter(conn => {
                if (this.isConnTypeOf(conn, "cable", "port")) {
                    const cable = this.cables.find(c => c.cableID === this.getIdOfTypeInConn(conn, "cable"));
                    const port = this.getPortOfDevice(items[i], this.getIdOfTypeInConn(conn, "port"));
                    if (!cable || !port) { return false; }
                    const sharedId = 'c_' + cable.cableID + 'p_' + port.portID; // REMOVE IF DISTINCT SELECT NOT NEEDED
                    if (itemSeenConns[sharedId]) { return false; } // REMOVE IF DISTINCT SELECT NOT NEEDED
                    itemSeenConns[sharedId] = true; // REMOVE IF DISTINCT SELECT NOT NEEDED
                    return port.portOrientation === "up" && cable.cableOrientation !== "up";
                }
                else {
                    return false;
                }
            }).length * this.leftCableSpace;
            const devTopY = devDrawnY - topExtraSpace;
            if (devTopY < minY) {
                minY = devTopY;
            }
        }
        return minY;
    }


    pinoutDevsToContainer() {
        for (let i = 0; i < this.pinoutDevPages.length; i++) {
            const devsOnCurrPage = this.pinoutDevPages[i];

            const itDev = Array.isArray(devsOnCurrPage) ? devsOnCurrPage.find(dev => dev.isIT) : null;

            let className = "pinout lightshadow";
            if (itDev && itDev.isIT === true) {
                className = "pinout lightshadow itDeviceToPinout";
            }

            let cont = d3.select(this.containerNode).append("svg")
                .attr("width", '100%')
                .attr("height", '800')
                .attr("viewBox", `0 0 ${this.width} ${this.height}`)
                .attr("class", className)
                .append("g");

            this.appendDefsToSVG(cont);

            if (itDev && itDev.text) {
                this.insertITNameText(cont, itDev.text);
            }
            else {
                this.insertSafetyText(cont);
            }
            this.addPageNumber(devsOnCurrPage.pageNum, cont);

            // devs pre-calc
            let currX = this.margin + this.devExtraMargin;
            for (const dev of devsOnCurrPage) {
                //this will decide where the dev will be drawn on the X axis
                dev.drawnX = currX + dev.marginLeft;

                currX += dev.marginLeft + dev.width + dev.marginRight + this.deviceDistance;
            }

            // cables pre-calc
            const topLineSpaceReservations = new PinoutCableLines();
            const bottomLineSpaceReservations = new PinoutCableLines();
            const drawnCables = {};
            // filter cables that have connections on current page
            const relevantCables = this.cables.filter(cable => {
                const cableID = cable.cableID;
                for (var device of devsOnCurrPage) {
                    const deviceConnCableIDs = device.connections
                        .filter(this.isConnTypeOf_C("port", "cable"))
                        .map(this.getIdOfTypeInConn_C("cable"));
                    if (deviceConnCableIDs.indexOf(cableID) != -1) {
                        return true;
                    }
                }
                return false;
            });

            // find out cable begin and end positions, and which line they will be in
            for (const cable of relevantCables) {
                let startX = this.margin, endX = this.width - this.margin, isStartModded = false, isEndModded = false;

                if (cable.beginningPort) {
                    // if dev is on this page and has the port
                    const devIdx = devsOnCurrPage.findIndex(dev => {
                        return dev.id == cable.beginningPort.deviceId && dev.ports.findIndex(port => port.portID == cable.beginningPort.portId) != -1;
                    });
                    if (devIdx != -1) {
                        const dev = devsOnCurrPage[devIdx];
                        const port = devsOnCurrPage[devIdx].ports.find(port => port.portID == cable.beginningPort.portId);
                        if (port) {
                            startX = dev.drawnX + (cable.cableOrientation === "up" ? port.xOriginPosition : port.xPosition);
                            isStartModded = true;
                        }
                    }
                }

                if (cable.endingPort) {
                    // if dev is on this page and has the port
                    const devIdx = devsOnCurrPage.findIndex(dev => {
                        return dev.id == cable.endingPort.deviceId && dev.ports.findIndex(port => port.portID == cable.endingPort.portId) != -1;
                    });
                    if (devIdx != -1) {
                        const dev = devsOnCurrPage[devIdx];
                        const port = devsOnCurrPage[devIdx].ports.find(port => port.portID == cable.endingPort.portId);
                        if (port) {
                            endX = dev.drawnX + (cable.cableOrientation === "up" ? port.xOriginPosition : port.xPosition);
                            isEndModded = true;
                        }
                    }
                }

                const seg = new PinoutCableSegment(startX, endX, cable);
                let yIdx;
                /* console.log(cable) */
                if (cable.cableOrientation === "up") {
                    yIdx = topLineSpaceReservations.placeSegment(seg);
                }
                else /* if (cable.cableOrientation === "down") */ {
                    yIdx = bottomLineSpaceReservations.placeSegment(seg);

                    cable.connectedPorts.filter(port => port.portOrientation === "up")
                        .forEach(port => {
                            /* const points = [
                                [item.drawnX + port.xOriginPosition, item.drawnY + port.yOriginPosition], //port pos on dev
                                [item.drawnX + port.xOriginPosition, item.drawnY + port.yPosition], // port to above dev
                                [item.drawnX + port.xPosition, item.drawnY + port.yPosition], // port pos top left
                            ]
                            const portSeg = new PinoutCableSegment(dev.drawn) */
                        })
                }

                cable.placement = { startX, endX, yIdx, isStartModded, isEndModded };

            }

            // set default space between all cables
            for (let i = 1; i < bottomLineSpaceReservations.lines.length; i++) {
                bottomLineSpaceReservations.lines[i].height = this.pinoutDevCablesDist;
            }

            for (let i = 1; i < topLineSpaceReservations.lines.length; i++) {
                topLineSpaceReservations.lines[i].height = this.leftCableSpace * 1.5;
            }

            // calc cable extra space for resistors
            for (const resi of this.resistors) {
                const ports = this.findResistorCables(resi, relevantCables, devsOnCurrPage);
                if (ports) {
                    const y1 = ports.cable1.placement.yIdx;
                    const y2 = ports.cable2.placement.yIdx;
                    const x1 = ports.cable1.placement.endX;
                    const x2 = ports.cable2.placement.endX;
                    const fakeSegment = new PinoutCableSegment(x1, x2);
                    const minY = y2 < y1 ? y2 : y1;
                    if (minY > 0 && !bottomLineSpaceReservations.lines[minY - 1].segmentFitsHere(fakeSegment)) {
                        bottomLineSpaceReservations.lines[minY].height = this.pinoutDevCablesDist * 1.5;
                    }
                }
            }

            // devices
            const dinRailTopY = (this.height - this.dinRailHeight) / 2;
            for (let j = 0; j < devsOnCurrPage.length; j++) {
                const dev = devsOnCurrPage[j];
                let devY = dinRailTopY + dev.yOffset;
                cont.append("image")
                    .attr("x", dev.drawnX)
                    .attr("y", devY)
                    .attr("width", dev.width)
                    .attr("height", dev.height)
                    .attr("xlink:href", `${getFrontendURL()}/${dev.imageUrl}`);
                dev.drawnY = devY;
                dev.bottomY = devY + dev.height;
            }

            // port names / port labels
            for (let device of devsOnCurrPage) {
                for (let port of device.ports) {
                    const portNameDisplayed = this.isPortNameDisplayed(device, port.portID);
                    if (port.label && port.label.displayName && portNameDisplayed) {
                        let boxX = device.drawnX + port.xOriginPosition + port.label.relativeX;
                        const boxY = device.drawnY + port.yOriginPosition + port.label.relativeY;
                        let anchor;
                        let isMiddle = false;
                        let isHorizontal = 'orientation' in port.label && port.label.orientation === "horizontal";
                        if (isHorizontal) {
                            anchor = "middle";
                            isMiddle = true;
                        }
                        else {
                            if (port.portOrientation == "up") {
                                anchor = "end";
                            }
                            else if (port.portOrientation == "down") {
                                anchor = "start";
                            }
                        }

                        if (!isMiddle) {
                            boxX += this.fontSizes.portName / 2 - this.fontSizes.portName * 0.1;
                        }

                        const portID = port.portID.replace("+", "PLUS");
                        let boxNode = cont.append("text")
                            .attr("x", boxX)
                            .attr("y", boxY)
                            .text(port.label.displayName)
                            .attr("text-anchor", anchor)
                            .attr("id", `i-${device.id}p-${portID}`)
                            .attr("font-size", this.fontSizes.portName)
                            .attr("transform", isMiddle ? null : `rotate(270 ${boxX} ${boxY})`)
                            .node();

                        let box = safeGetBBox(boxNode);

                        const boxMargin = isHorizontal ? 2 : 0.6;
                        const fill = 'background' in port.label ? port.label.background : '#ffffff';
                        cont.insert("rect", `#i-${device.id}p-${portID}`)
                            .attr("x", box.x - boxMargin)
                            .attr("y", box.y + this.fontSizes.portName * 0.1)
                            .attr("width", box.width + boxMargin * 2)
                            .attr("height", box.height - this.fontSizes.portName * 0.2)
                            .attr("fill", fill)
                            .attr("fill-opacity", "0.8")
                            .attr("transform", isMiddle ? null : `rotate(270 ${boxX} ${boxY})`);
                    }
                }
            }

            const lowestDeviceY = this.getLowestDeviceY(devsOnCurrPage);
            const highestDeviceY = this.getHighestDeviceY(devsOnCurrPage);
            // cables
            for (let j = 0; j < relevantCables.length; j++) {
                const cable = relevantCables[j];

                const startX = cable.placement.startX;
                const endX = cable.placement.endX;
                const yIdx = cable.placement.yIdx;

                let currY;
                if (cable.cableOrientation === "up") {
                    currY = highestDeviceY - this.leftCableSpace - topLineSpaceReservations.heightSumUntilY_TOP(yIdx);
                }
                else {
                    currY = lowestDeviceY + this.cableDevSpace + bottomLineSpaceReservations.heightSumUntilY_TOP(yIdx);
                }

                drawnCables[cable.cableID] = {};
                drawnCables[cable.cableID].line = cont.append("line")
                    .attr("x1", startX)
                    .attr("y1", currY)
                    .attr("x2", endX)
                    .attr("y2", currY)
                    .attr("stroke", cable.cableColor)
                    .attr("stroke-width", this.cableStroke);

                cable.drawnY = currY;
            }

            // connections
            for (let item of devsOnCurrPage) {
                const labels = item.labels;
                if (typeof labels !== 'undefined' && labels.length > 0) {
                    for (let label of labels) {
                        let text = cont.append("text")
                            .attr("x", item.drawnX + label.x)
                            .attr("y", item.drawnY + label.y)
                            //.attr("transform", `rotate(${rotation} ${textX} ${textY})`)
                            .attr("font-size", 4)
                            .attr("class", "labels-text");
                        text.append("tspan")
                            .text(label.text);
                        const textBoxNode = text.node();
                        const textBox = safeGetBBox(textBoxNode);
                        cont.insert("rect", ".port-text")
                          .attr('x', textBox.x - 1.5)
                          .attr('y', textBox.y - 1.5)
                          .attr('width', textBox.width + 3)
                          .attr('height', textBox.height + 3)
                          .attr('fill', "#ffffff")
                          .attr('fill-opacity', "0.8");
                    }



                }
                item.usedPorts = {};
                // console.log( 'CONNECTIONS', item )
                for (let conn of item.connections) {
                  console.log( conn )
                    if (this.doesConnHaveType(conn, "port")) {
                        const portID = this.getIdOfTypeInConn(conn, "port");
                        const port = item.ports.find(port => port.portID == portID);
                        if (portID in item.usedPorts) {
                            this.warn(`Port with ID "${portID}" is already used in device "${item.id}", skipping...`);
                            continue;
                        }
                        if (!port) {
                            this.warn(`Couldn't find port with ID "${portID}" in device "${item.id}", skipping...`);
                            continue;
                        }
                        if (this.isConnTypeOf(conn, "cable", "port")) {
                            const cableID = this.getIdOfTypeInConn(conn, "cable");

                            let portXPos;
                            let portYPos;
                            const cable = this.cables.find(cable => cable.cableID == cableID);
                            if (port.portOrientation === "up" && cable && cable.cableOrientation === "up") {
                                portYPos = port.yOriginPosition;
                                portXPos = port.xOriginPosition;
                            }
                            else {
                                portYPos = port.yPosition;
                                portXPos = port.xPosition;
                            }

                            if (cable) {
                                // append connection line
                                const connX = item.drawnX + portXPos;
                                const connY = cable.drawnY;
                                cont.append("line")
                                    .attr("x1", connX)
                                    .attr("y1", item.drawnY + portYPos)
                                    .attr("x2", connX)
                                    .attr("y2", connY)
                                    .attr("stroke", cable.cableColor)
                                    .attr("stroke-width", this.cableStroke);

                                // append circle if needed
                                if (!(cable.beginningPort && cable.beginningPort.deviceId == item.id && cable.beginningPort.portId == port.portID ||
                                    cable.endingPort && cable.endingPort.deviceId == item.id && cable.endingPort.portId == port.portID)) {
                                    cont.append("circle")
                                        .attr("cx", connX)
                                        .attr("cy", connY)
                                        .attr("r", this.cableStroke * 1.5)
                                        .attr("fill", "white")
                                        .attr("stroke", cable.cableColor)
                                        .attr("stroke-width", this.cableStroke);
                                }

                                // if port is upper and cable is not -> connect origin to line
                                if (port.portOrientation == "up" && cable.cableOrientation !== "up") {
                                    const points = [
                                        [item.drawnX + port.xOriginPosition, item.drawnY + port.yOriginPosition], //port pos on dev
                                        [item.drawnX + port.xOriginPosition, item.drawnY + port.yPosition], // port to above dev
                                        [item.drawnX + port.xPosition, item.drawnY + port.yPosition], // port pos top left
                                    ]
                                    cont.append("polyline")
                                        .attr("points", points.join(" "))
                                        .attr("stroke", cable.cableColor)
                                        .attr("stroke-width", this.cableStroke)
                                        .attr("fill", "none");
                                }
                            }
                        }
                        if ("additionalText" in conn) {

                          const place = port.portOrientation || "down"; // (up/down)
                          const textDirection = conn.textOrientation || "normal";  // (normal, reversed)
                          let rotation = (conn.textRotation || 270) + (textDirection == "reversed") * 180;
                      
                          const isCable = this.doesConnHaveType(conn, "cable");
                      
                          let xPos, yPos;
                      
                          if (isCable) {
                              if (rotation != 270) {
                                  this.errors.push("Rotation of port-cable connections can only be the default value. (270) (Auto-fixed)");
                                  rotation = 270;
                              }
                              xPos = port.xPosition;
                              yPos = port.yPosition;
                          } else {
                              xPos = port.xOriginPosition;
                              yPos = port.yOriginPosition;
                          }
                      
                          const menorahGroup = this.getMenorahGroup(item, port.portID);
                          let fontSize = this.fontSizes.portAdditionalText;
                          if (menorahGroup) {
                              fontSize = this.fontSizes.menorahAdditionalText;
                          } else if (item.isIT) {
                              fontSize = this.fontSizes.itAdditionalText;
                          }
                          /* const fontSize = menorahGroup ? this.fontSizes.menorahAdditionalText : (this.fontSizes.portAdditionalText); */
                          const menorahIdx = this.getMenorahIdx(menorahGroup, port.portID);
                          const menorahLineColor = this.getMenorahLineColor(menorahGroup, port.portID);
                          const lineStroke = menorahLineColor || "black";
                          const lineStrokeWidth = menorahLineColor ? this.cableStroke * 0.8 : this.cableStroke;
                      
                          console.log(conn.targetId);
                      
                          const portStartX = item.drawnX + xPos;
                          const portStartY = item.drawnY + yPos;
                      
                          let portEndY = 0;
                          let portEndYTransform = 0;
                      
                          if (menorahGroup) {
                              // Basically line up every port so they are starting from the same horizon. This is done by taking the uppermost port of the group.
                              portEndY = item.drawnY + this.getMenorahGroupBaseY(item, menorahGroup);
                              portEndYTransform -= Math.abs(menorahIdx) * this.menorahDistanceIncrementY - menorahGroup.length / 2 * this.menorahDistanceIncrementY - 10 - 3;
                          } else {
                              portEndY = portStartY;
                              portEndYTransform = this.portPlainTextDistance;
                          }
                      
                          if ('lengthMult' in port) {
                              portEndYTransform *= port.lengthMult;
                          }
                      
                          if (place == "up") {
                              portEndY -= portEndYTransform;
                          } else if (place == "down") {
                              portEndY += portEndYTransform;
                          }
                        
                      
                          // plainText or bus
                          if (!isCable) {
                              cont.append("line")
                                  .attr("x1", portStartX)
                                  .attr("y1", portStartY)
                                  .attr("x2", portStartX)
                                  .attr("y2", portEndY)
                                  .attr("stroke", lineStroke)
                                  .attr("stroke-width", lineStrokeWidth);
                          }
                      
                          const anchors = { "1": "start", "-1": "end" };
                          let anchorIdx = -1;
                          if (place == "up") {
                              anchorIdx *= -1;
                          }
                          if (textDirection == "reversed") {
                              anchorIdx *= -1;
                          }
                          let anchor = anchors[anchorIdx.toString()];
                      
                          let textX = portStartX + uMath.cosD(uMath.degToRad(rotation)) * this.rasterSize / 3 * (textDirection == "reversed" ? -1 : 1);
                          let textY = portEndY + (place == "down" ? 2 : -2);
                          if (textDirection == "reversed") {
                              textX += place == "up" ? 2 : -2;
                              textY += place == "up" ? 4 : -4;
                          }
                      
                          if (isCable) {
                              textX -= fontSize / 2 + 1;
                          } else if (menorahGroup) {
                              textX += menorahIdx * this.menorahDistanceIncrementX;
                              textY -= Math.abs(menorahIdx) * this.menorahDistanceIncrementY;
                              const extraLineX = portStartX + menorahIdx * this.menorahDistanceIncrementX;
                              const lineData = [
                                  // { x: portStartX, y: portEndY + pathShiftY },
                                  // { x: extraLineX, y: portEndY + pathShiftY },
                                  { x: portStartX, y: portEndY },
                                  { x: extraLineX, y: portEndY },
                                  { x: extraLineX, y: textY }
                              ];
                              const lineFunction = d3.line()
                                .x(function (d) { return d.x; })
                                .y(function (d) { return d.y; })
                                .curve(d3.curveLinear);
                      
                              cont.append("path")
                                  .attr("d", lineFunction(lineData))
                                  .attr("stroke", lineStroke)
                                  .attr("stroke-width", lineStrokeWidth)
                                  .attr("fill", "none");
                      
                              textX -= fontSize * 0.2;
                          }
                      
                          const text = cont.append("text")
                              .attr("x", textX)
                              .attr("y", textY)
                              .attr("text-anchor", anchor)
                              .attr("transform", `rotate(${rotation} ${textX} ${textY})`)
                              .attr("font-size", fontSize)
                              .attr("class", "port-text");
                      
                          if (menorahGroup) {
                              text.append("tspan")
                                  .attr("fill", "gray")
                                  .text(port.label.displayName + " ");
                          }
                      
                          text.append("tspan")
                              .text(conn.additionalText);
                      
                          if (conn.additionalText) {
                              const textBoxNode = text.node();
                              const textBox = safeGetBBox(textBoxNode);
                              cont.insert("rect", ".port-text")
                                  .attr('x', textBox.x)
                                  .attr('y', textBox.y + 3)
                                  .attr('width', textBox.width)
                                  .attr('height', textBox.height - 3)
                                  .attr('fill', "#ffffff")
                                  .attr('fill-opacity', "0.8")
                                  .attr('transform', `rotate(${rotation} ${textX} ${textY})`);
                          }
                      }
                      
                        item.usedPorts[portID] = true;
                    }
                }
            }

            // resistors
            for (let resi of this.resistors) {
                const ports = this.findResistorCables(resi, relevantCables, devsOnCurrPage);

                if (ports) {
                    const cable1X = ports.cable1.placement.endX;
                    const cable2X = ports.cable2.placement.endX;
                    const cable1Y = ports.cable1.drawnY;
                    const cable2Y = ports.cable2.drawnY;
                    let resiCenterY;
                    if (cable1Y > cable2Y) {
                        resiCenterY = cable1Y + this.rasterSize;
                    }
                    else {
                        resiCenterY = cable2Y + this.rasterSize;
                    }
                    let leftCableX, rightCableX;
                    if (cable1X < cable2X) {
                        leftCableX = cable1X;
                        rightCableX = cable2X;
                    }
                    else {
                        leftCableX = cable2X;
                        rightCableX = cable1X;
                    }
                    // draw line behind
                    cont.append("line")
                      .attr('x1', leftCableX - this.cableStroke / 2)
                      .attr('y1', resiCenterY)
                      .attr('x2', rightCableX + this.cableStroke / 2)
                      .attr('y2', resiCenterY)
                      .attr('stroke', "black")
                      .attr('stroke-width', this.cableStroke);

                    // draw rectangle
                    const resistorHeight = this.pinoutDevCablesDist / 3;
                    this.drawRect(cont, cable1X, cable2X, resiCenterY, resistorHeight, "red", "red", 0, this.cableStroke);

                    // draw left, right line
                    cont.append("line")
                      .attr('x1', cable1X)
                      .attr('y1', cable1Y)
                      .attr('x2', cable1X)
                      .attr('y2', resiCenterY)
                      .attr('stroke', "black")
                      .attr('stroke-width', this.cableStroke);

                    cont.append("line")
                      .attr('x1', cable2X)
                      .attr('y1', cable2Y)
                      .attr('x2', cable2X)
                      .attr('y2', resiCenterY)
                      .attr('stroke', "black")
                      .attr('stroke-width', 1);

                    // draw text
                    cont.append("text")
                      .text(resi.text)
                      .attr('x', (cable1X + cable2X) / 2)
                      .attr('y', resiCenterY + resistorHeight / 2 + this.fontSizes.resistorText + 1)
                      .attr('font-size', this.fontSizes.resistorText)
                      .attr('text-anchor', "middle");
                }
            }

            for (const cable of relevantCables) {
                cont.append("text")
                    .text(cable.cableName)
                    .attr("x", cable.placement.startX + (cable.placement.isStartModded ? 2 : 0))
                    .attr("y", cable.drawnY - 3)
                    .attr("font-size", this.fontSizes.cableName);

            }
        }
    }

    pageStringFromArray(array, separator = "-") {
        if (array.length == 1) {
            return array[0];
        }
        else {
            const first = array[0];
            const last = array[array.length - 1];
            if (first == last) {
                return first;
            }
            else {
                return `[${first}${separator}${last}]`;
            }
        }
    }

    generatePages() {
        // generate actual pages
        this.pinoutDevPages = this.pagesFromPinoutDevs();
        this.busPages_old = this.pagesFromBuses();
        this.busPages = this.pagesFromTreeBuses();

        // number them
        let pageNum = 0;
        this.pinoutDevPages.forEach(page => {
            if (Array.isArray(page) && page.length > 0) {
                page.pageNum = ++pageNum;
            }
        });

        this.busPages.forEach(page => {
            if (page && Array.isArray(page.lines) && page.lines.length > 0) {
                page.pageNum = ++pageNum;
            }
        });

        this.busPages_old.forEach(page => {
            if (page && Array.isArray(page.lines) && page.lines.length > 0) {
                page.pageNum = ++pageNum;
            }
        });

        this.pageNum = pageNum;

        // connect bus numbers from pinoutdevs to buses
        const busDrawPages = {};

        this.busPages.forEach(page => {
            page.lines.forEach(line => {
                const lineId = line.original.id;
                if (lineId in busDrawPages) {
                    busDrawPages[lineId].push(page.pageNum);
                } else {
                    busDrawPages[lineId] = [page.pageNum];
                }
            });
        });

        this.busPages_old.forEach(page => {
            page.lines.forEach(line => {
                const lineId = line.original.id;
                if (lineId in busDrawPages) {
                    busDrawPages[lineId].push(page.pageNum);
                } else {
                    busDrawPages[lineId] = [page.pageNum];
                }
            });
        });

        // add page numbers to big device texts
        this.pinoutDevPages.forEach(page => {
            page.forEach(dev => {
                dev.connections.forEach(conn => {
                    if (this.doesConnHaveType(conn, "bus") && this.getIdOfTypeInConn(conn, "bus") in busDrawPages) {
                        if (conn.additionalText) {
                            const pages = PINOUT_PAGENUM_PREFIX + this.pageStringFromArray(busDrawPages[this.getIdOfTypeInConn(conn, "bus")]);
                            /* conn.additionalText += ` ( ${PINOUT_PAGENUM_PREFIX}${pages})`; */
                            conn.additionalText += ' (' + ts2('pinout_see_section', 'see section {pagenum}', { pagenum: pages }) + ')';
                        }
                    }
                });
            });
        });
    }

    printErrors() {
        for (const err of this.errors) {
            console.log(err);
        }
    }

    getPageNum() {
        return this.pageNum;
    }

    resultToContainer() {
        if (!this.pinoutDevices) {
            console.log('not drawing since there are no devices');
            return;
        }
        d3.select(this.containerNode).text("");
        d3.select(this.containerNode).html("");
        this.errors = [];
        this.sortCables();
        this.filterMenorahGroups();
        this.generatePages();
        this.pinoutDevsToContainer();
        this.treeToContainer();
        this.busesToContainer();
        $('.pinout').each(function () {
            $(this).height($(this).width() * 595 / 842);
        });
        this.printErrors();
    }
}

/**
 * @typedef {Object} CableListDevice
 * @property {string} device_name
 * @property {CableListPort[]} ports
 */

/**
 * @typedef {Object} CableListPort
 * @property {string} port_name
 * @property {boolean} bus
 * @property {boolean} attachedToPrev
 * @property {boolean} attachedToNext
 * @property {boolean} firstOnPage
 * @property {boolean} lastOnPage
 * @property {string} cable_type_name
 * @property {string|string[]} label_name
 */

/**
* @typedef {Object} StandardPageElem
* @property {"port" | "device"} type
* @property {CableListPort | CableListDevice} original
* @property {number} size
*/

export class QuoteCableList {

    /**
     *
     * @param {JQuery<HTMLElement>} container
     * @param {CableListDevice[]} data
     */
    constructor(container, data, prevPageNum) {

        /**
         * @type {CableListDevice[]}
         */
        this.cableListData = this.transformData(data);
        /**
         * The jQuery selected container where we will place the pinout pages.
         * @type {JQuery<HTMLElement>}
         */
        this.$container = container;
        /**
         * The DOM Element where we will place the pinout pages.
         */
        this.containerNode = this.$container[0];
        this.prevPageNum = $.isNumeric(prevPageNum) ? +prevPageNum : 0;
        /**
         * Width of a page in pixels (A4 is 11.693 inches wide, DPI is 72 -> 11.693*72 = 842).
         */
        this.width = 842;
        /**
         * Height of a page in pixels (A4 is 8.268 inches tall, DPI is 72 -> 8.268*72 = 595).
         */
        this.height = 595;
        /**
         * Width and height of a square on the grid in pixels.
         * Changing this scales the whole image up or down, since the other variables depend on it.
         */
        this.rasterSize = 8;
        /**
         * Font size parameters.
         */
        this.fontSizes = {
            deviceName: this.rasterSize * 2,
            portNLabelName: this.rasterSize * 1.5,
            cableTypeName: this.rasterSize * 1.25,
            /**
             * The text that tells the customers that this is not a 100% correct plan.
             */
            safetyText: this.rasterSize,
        }
        /**
         * Margin at the top/bottom/left/right sides of the page.
         */
        this.margin = this.rasterSize * 3;

        this.deviceNameMargin = {
            top: this.rasterSize,
            bottom: this.rasterSize
        }
        this.portNameMargin = {
            top: 0,
            bottom: this.rasterSize * 2,
            left: this.rasterSize,
            right: this.rasterSize * 2
        }
        this.labelNameMargin = {
            left: this.rasterSize * 2,
            right: this.rasterSize * 2
        }

        this.currGroupId = 0;
        this.groupProps = {};

        /**
         * @type {StandardPageElem[][]}
         */
        this.pages = [];
    }

    getGroupFullLength(size) {
        const sizePerElem = 12;
        const maxWidth = 8;
        if (size <= maxWidth) {
            return size * sizePerElem;
        }
        else {
            return maxWidth * sizePerElem;
        }
    }

    /**
     *
     * @param {CableListDevice[]} data
     */
    transformData(data) {
        if (!Array.isArray(data)) {
            return [];
        }
        return data.filter(function (elem) {
            if (!Array.isArray(elem.ports)) {
                return false;
            }
            elem.ports = elem.ports.filter(port => {
                if (!(typeof port == "object")) {
                    return false;
                }
                if (!("cable_type_name" in port && "label_name" in port && "port_name" in port)) {
                    return false;
                }

                if (!("bus" in port)) {
                    port.bus = false;
                }

                if (port.bus) {
                    return Array.isArray(port.label_name) && port.label_name.length != 0;
                }
                else {
                    if (typeof port.label_name == "string") {
                        port.label_name = [port.label_name]
                    }
                    return port.label_name[0].length != 0;
                }
            });
            if (elem.ports.length == 0) {
                return false;
            }
            return true;
        });
    }

    /**
     *
     * @param {d3.Selection<any>} container
     */
    insertSafetyText(container) {
        container.insert("text")
          .text(ts("q_safety_text", "This is not an actual electrical plan. Please contact your electrician for guidance."))
          .attr('font-size', this.fontSizes.safetyText)
          .attr('x', this.width - this.margin * 2)
          .attr('y', this.margin / 2 + this.fontSizes.safetyText / 2)
          .attr('text-anchor', "end")
          .attr('fill', "lightgrey");
    }

    /**
     *
     * @param {d3.Selection} cont
     */
    addPageNumber(number, cont) {
        PinoutAddPageNumber(number, cont, this);
    }

    getVerticalSizeOfDeviceName() {
        return this.fontSizes.deviceName + this.deviceNameMargin.top + this.deviceNameMargin.bottom;
    }

    getVerticalSizeOfPortName() {
        return this.fontSizes.portNLabelName + this.portNameMargin.top + this.portNameMargin.bottom;
    }

    getPageAvailableVerticalSpace() {
        return this.height - this.margin * 2;
    }

    /**
     *
     * @param {d3.Selection<any>} svg
     * @returns {d3.Selection<any>}
     */
    appendDefsToSVG(svg) {
        svg.append('defs')
            .append('marker')
            .attr('id', 'head')
            .attr('orient', 'auto')
            .attr('markerWidth', '4')
            .attr('markerHeight', '4')
            .attr('refX', '0.1')
            .attr('refY', '2')
            .append('path')
            .attr('d', 'M0,0 V4 L4,2 Z')
            .attr('fill', 'black')
        return svg;
    }

    generatePages() {
        const elems = [];
        this.cableListData.forEach(device => {
            elems.push({ type: 'device', original: device, size: this.getVerticalSizeOfDeviceName() });
            const portClones = [];
            device.ports.forEach(port => {
                port.label_name.forEach((label, idx, arr) => {
                    const portClone = Object.assign({}, port);
                    portClone.label_name = label;
                    if (idx > 0) {
                        portClone.attachedToPrev = true;
                    }
                    else {
                        portClone.attachedToPrev = false;
                    }

                    if (arr.length > 1 && idx < arr.length - 1) {
                        portClone.attachedToNext = true;
                    }
                    else {
                        portClone.attachedToNext = false;
                    }
                    elems.push({ type: 'port', original: portClone, size: this.getVerticalSizeOfPortName() });
                    portClones.push(portClone);
                });
                /* elems.push({ type: 'port', original: port, size: this.getVerticalSizeOfPortName() }) */
            });
            try {
                if (portClones.length > 0) {
                    let lastSeenPortName = portClones[0].cable_type_name + " " + portClones[0].label_name;
                    let idxInGrp = 0;
                    portClones[0].indexInGroup = idxInGrp++;
                    portClones[0].groupId = ++this.currGroupId;
                    this.groupProps[this.currGroupId] = { len: idxInGrp };
                    for (let i = 1; i < portClones.length; i++) {
                        const currPortName = portClones[i].cable_type_name + " " + portClones[i].label_name;
                        if (currPortName !== lastSeenPortName) {
                            this.groupProps[this.currGroupId] = { len: idxInGrp };

                            this.currGroupId++;
                            idxInGrp = 0;

                            portClones[i].indexInGroup = idxInGrp++;
                            portClones[i].groupId = this.currGroupId;

                            lastSeenPortName = currPortName;
                            this.groupProps[this.currGroupId] = { len: idxInGrp };
                        }
                        else {
                            portClones[i].indexInGroup = idxInGrp++;
                            portClones[i].groupId = this.currGroupId;
                            this.groupProps[this.currGroupId] = { len: idxInGrp };
                        }
                    }

                    portClones.forEach(port => {
                        if (this.groupProps[port.currGroupId] &&
                            this.groupProps[port.currGroupId].len > 1 &&
                            port.cable_type_name.trim() === "") {
                            port.cable_type_name = "_";
                        }
                    });
                }
            } catch (err) {
                console.debug(err);
            }
        });

        const maxHeight = this.getPageAvailableVerticalSpace();
        const pages = [];
        let currentPage = [];
        pages.push(currentPage);
        let currentCapacity = 0;

        elems.forEach((elem, idx) => {
            elem.original.firstOnPage = false;
            elem.original.lastOnPage = false;
            if (currentCapacity + elem.size <= maxHeight) {
                if (currentPage.length == 0) {
                    elem.original.firstOnPage = true;
                }
                currentPage.push(elem);
                currentCapacity += elem.size;
            }
            else {
                currentCapacity = elem.size;
                currentPage = [elem];
                elem.original.firstOnPage = true;
                if (idx - 1 > 0) {
                    elems[idx - 1].original.lastOnPage = true;
                }
                pages.push(currentPage);
            }
        });
        if (pages.length == 1 && currentPage.length == 0) {
            return [];
        }
        this.pages = pages;
        return pages;
    }

    getOutputPolylinePointsAround(rect) {
        return [
            [rect.x, rect.y], // top left
            [rect.x + rect.width, rect.y], // top right
            [rect.x + rect.width + rect.height / 2, rect.y + rect.height / 2], // right center
            [rect.x + rect.width, rect.y + rect.height], // bottom right
            [rect.x, rect.y + rect.height],  // bottom left
            [rect.x, rect.y], // top left again
        ];
    }

    resultToContainer() {
        d3.select(this.containerNode).text("");
        d3.select(this.containerNode).html("");

        this.generatePages();
        this.pages.forEach((page, idx) => {
            let pageCont = d3.select(this.containerNode).append("svg")
                .attr("width", '100%')
                .attr("height", '800')
                .attr("viewBox", `0 0 ${this.width} ${this.height}`)
                .attr("class", "pinout lightshadow")
                .append("g");

            this.appendDefsToSVG(pageCont);

            this.addPageNumber(this.prevPageNum + idx + 1, pageCont);

            this.insertSafetyText(pageCont);

            let currY = this.margin;

            page.forEach(elem => {
                if (elem.type == "device") {
                    currY += this.deviceNameMargin.top;
                    currY += this.fontSizes.deviceName;

                    // DEVICE NAME
                    pageCont.append('text')
                      .attr('x', this.margin)
                      .attr('y', currY)
                      .attr('font-size', this.fontSizes.deviceName)
                      .attr('fill', 'black')
                      .attr('text-anchor', 'start')
                      .text(elem.original.device_name);

                    currY += this.deviceNameMargin.bottom;
                }
                else if (elem.type == "port") {
                    const ori = elem.original;
                    // PORT NAME
                    currY += this.portNameMargin.top;
                    currY += this.fontSizes.portNLabelName;
                    let portNameBBox;

                    if (!elem.original.attachedToPrev) {
                        const portName = pageCont.append('text')
                          .attr('x', this.margin + this.portNameMargin.left)
                          .attr('y', currY)
                          .attr('font-size', this.fontSizes.portNLabelName)
                          .attr('fill', 'black')
                          .attr('text-anchor', 'start')
                          .text(elem.original.port_name).node();

                        portNameBBox = safeGetBBox(portName);
                    }

                    // LABEL NAME
                    const labelName = pageCont.append('text')
                      .attr('x', this.width - this.margin - this.labelNameMargin.right)
                      .attr('y', currY)
                      .attr('font-size', this.fontSizes.portNLabelName)
                      .attr('fill', 'black')
                      .attr('text-anchor', 'end')
                      .text(ori.label_name).node();



                    const labelNameBBox = safeGetBBox(labelName);

                    var bboxWithMargin = {
                        x: labelNameBBox.x - 2,
                        y: labelNameBBox.y,
                        width: labelNameBBox.width,
                        height: labelNameBBox.height,
                    }

                    // LABEL OUTPUT POLYLINE
                    const endPolyLinePoints = this.getOutputPolylinePointsAround(bboxWithMargin);

                    const lineFunction = d3.line()
                      .x(function (d) { return d[0]; })
                      .y(function (d) { return d[1]; })
                      .curve(d3.curveLinear);

                    pageCont.append("path")
                        .attr("d", lineFunction(endPolyLinePoints))
                        .attr("stroke", 'black')
                        .attr("stroke-width", 0.5)
                        .attr("fill", "none");

                    // CONNECT TO PREV - unsupported now
                    if (elem.original.attachedToPrev) {
                        const prevConnWireX = labelNameBBox.x - 2 - 10;
                        const prevConnWireY1 = labelNameBBox.y + labelNameBBox.height / 2;
                        const connBackLen = elem.original.firstOnPage ? this.getVerticalSizeOfPortName() / 2 : this.getVerticalSizeOfPortName();
                        const prevConnWireY2 = labelNameBBox.y + labelNameBBox.height / 2 - connBackLen;
                        pageCont.append('line')
                          .attr('x1', prevConnWireX)
                          .attr('y1', prevConnWireY1)
                          .attr('x2', prevConnWireX)
                          .attr('y2', prevConnWireY2)
                          .attr('stroke', 'black')
                          .attr('stroke-width', 0.5);

                        pageCont.append('line')
                          .attr('x1', prevConnWireX)
                          .attr('y1', prevConnWireY1)
                          .attr('x2', labelNameBBox.x - 2)
                          .attr('y2', prevConnWireY1)
                          .attr('stroke', 'black')
                          .attr('stroke-width', 0.5);
                    }

                    // unsupported now
                    if (elem.original.attachedToNext && elem.original.lastOnPage) {
                        const prevConnWireX = labelNameBBox.x - 2 - 10;
                        const prevConnWireY1 = labelNameBBox.y + labelNameBBox.height / 2;
                        const prevConnWireY2 = labelNameBBox.y + labelNameBBox.height / 2 + this.getVerticalSizeOfPortName() / 2;
                        pageCont.append('line')
                          .attr('x1', prevConnWireX)
                          .attr('y1', prevConnWireY1)
                          .attr('x2', prevConnWireX)
                          .attr('y2', prevConnWireY2)
                          .attr('stroke', 'black')
                          .attr('stroke-width', 0.5);
                    }

                    // WIRE BETWEEN PORT AND LABEL
                    let wire;

                    if (!elem.original.attachedToPrev) {
                        wire = {
                            x1: Math.max(this.fontSizes.portNLabelName * 6, portNameBBox.x + portNameBBox.width + this.portNameMargin.right),
                            y: portNameBBox.y + portNameBBox.height / 2,
                            /* x2: labelNameBBox.x - this.labelNameMargin.left, */
                            x2: labelNameBBox.x - 2
                        };
                    }

                    if (!elem.original.attachedToPrev) {
                        pageCont.append('line')
                          .attr('x1', wire.x1)
                          .attr('y1', wire.y)
                          .attr('x2', wire.x2)
                          .attr('y2', wire.y)
                          .attr('stroke', 'black')
                          .attr('stroke-width', 0.5);
                    }

                    // WIRE START MARKER
                    if (!elem.original.attachedToPrev) {
                        const markerDiff = this.fontSizes.portNLabelName / 2 * 0.8;
                        const startPt = { x: wire.x1 + markerDiff * 0.8, y: wire.y };
                        for (let i of [-1, 0, 1]) {
                            pageCont.append('line')
                              .attr('x1', startPt.x)
                              .attr('y1', startPt.y)
                              .attr('x2', wire.x1)
                              .attr('y2', startPt.y + i * markerDiff)
                              .attr('stroke', 'black')
                              .attr('stroke-width', 1.5);
                        }
                    }

                    /* const center = uMath.segmentCenter(wire); */
                    if (!elem.original.attachedToPrev) {
                        try {
                            let cable_text = ori.cable_type_name;

                            if (!ori.firstOnPage && 'indexInGroup' in ori && ori.indexInGroup !== 0) {
                                cable_text = "";
                            }

                            // add text
                            if (cable_text.length) {
                                const cableNameNode = pageCont.append('text')
                                  .attr('x', this.width / 2)
                                  .attr('y', wire.y - this.rasterSize * 5 / 8)
                                  .attr('font-size', this.fontSizes.cableTypeName)
                                  .attr('fill', 'black')
                                  .attr('text-anchor', 'middle')
                                  .text(cable_text).node();

                                const cableNameBBox = safeGetBBox(cableNameNode);

                                // underline text if has more elements
                                if (cableNameBBox && 'groupId' in ori &&
                                    this.groupProps[ori.groupId] &&
                                    this.groupProps[ori.groupId].len > 1 &&
                                    ori.indexInGroup === 0) {
                                    const textLowY = cableNameBBox.y + cableNameBBox.height;
                                    pageCont.append('line')
                                      .attr('x1', cableNameBBox.x)
                                      .attr('y1', textLowY)
                                      .attr('x2', cableNameBBox.x + cableNameBBox.width)
                                      .attr('y2', textLowY)
                                      .attr('stroke-width', 0.5)
                                      .attr('stroke', 'black');
                                    this.groupProps[ori.groupId].textEndX = cableNameBBox.x + cableNameBBox.width;
                                    this.groupProps[ori.groupId].textEndY = textLowY;
                                    this.groupProps[ori.groupId].wireTextYDiff = wire.y - textLowY;
                                }
                            }
                        }
                        catch (err) {
                            console.debug(err);
                        }
                        try {
                            // add line
                            if ('indexInGroup' in ori && 'groupId' in ori &&
                                this.groupProps[ori.groupId] && this.groupProps[ori.groupId].len > 1) {
                                const grp = this.groupProps[ori.groupId];
                                const groupLineLength = this.getGroupFullLength(grp.len);
                                const groupLineLengthPerElem = groupLineLength / grp.len;
                                const yDistBtwnWires = this.fontSizes.portNLabelName + this.portNameMargin.bottom;
                                const smallXDist = (groupLineLengthPerElem * grp.wireTextYDiff) / (yDistBtwnWires);
                                if (ori.indexInGroup === 0) {
                                    const endPt = {
                                        x: grp.textEndX + smallXDist,
                                        y: wire.y
                                    };

                                    if (ori.lastOnPage) {
                                        endPt.y += (this.fontSizes.portNLabelName + this.portNameMargin.bottom) * 0.33;
                                        endPt.x += groupLineLengthPerElem * 0.33;
                                    }

                                    if (!(isNaN(grp.textEndX) || isNaN(grp.textEndY) || isNaN(endPt.x) || isNaN(endPt.y))) {
                                        pageCont.append('line')
                                          .attr('x1', uMath.clamp(grp.textEndX, 75, 720))
                                          .attr('y1', grp.textEndY)
                                          .attr('x2', uMath.clamp(endPt.x, 75, 720))
                                          .attr('y2', endPt.y)
                                          .attr('stroke-width', 0.5)
                                          .attr('stroke', 'black');
                                    }
                                }
                                else {
                                    const baseX = grp.textEndX + smallXDist;
                                    //portNameBBox.y + portNameBBox.height / 2,

                                    let startPt = {
                                        x: baseX + groupLineLengthPerElem * (ori.indexInGroup - 1),
                                        y: wire.y - this.fontSizes.portNLabelName - this.portNameMargin.bottom
                                    };

                                    let endPt = {
                                        x: baseX + groupLineLengthPerElem * ori.indexInGroup,
                                        y: wire.y
                                    };

                                    if (ori.firstOnPage && ori.indexInGroup > 0) {
                                        let inZero = uMath.subtractPoints(endPt, startPt);
                                        inZero = uMath.multiplyScalarPoint(0.66, inZero);
                                        startPt = uMath.addPoints(startPt, inZero);
                                    }
                                    else if (ori.lastOnPage && ori.indexInGroup !== grp.len - 1) {
                                        let inZero = uMath.subtractPoints(endPt, startPt);
                                        inZero = uMath.multiplyScalarPoint(0.33, inZero);
                                        endPt = uMath.addPoints(endPt, inZero);
                                    }

                                    if (!(isNaN(startPt.x) || isNaN(startPt.y) || isNaN(endPt.x) || isNaN(endPt.y))) {
                                        pageCont.append('line')
                                          .attr('x1', uMath.clamp(startPt.x, 75, 720))
                                          .attr('y1', startPt.y)
                                          .attr('x2', uMath.clamp(endPt.x, 75, 720))
                                          .attr('y2', endPt.y)
                                          .attr('stroke-width', 0.5)
                                          .attr('stroke', 'black');
                                    }
                                }
                            }
                        }
                        catch (err) {
                            console.debug(err);
                        }
                    }
                    currY += this.portNameMargin.bottom;
                }
            });
        });

        $('.pinout').each(function () {
            $(this).height($(this).width() * 595 / 842);
        });
    }
}
